// The animator — pure, environment-agnostic sampling of an {@link Animation}
// at a moment in time. It has no three.js or DOM dependency, so it is trivially
// testable and can drive any renderer. `instantiateModel` (which *does* use
// three.js) feeds the result into the scene graph each frame.

import type { Animation, Easing, Interaction, Keyframe, Track, Vec3 } from "./types";
import { ease } from "./easing";

/** The resolved local transform of one animated node at a sampled time. */
export interface NodeTransform {
  /** Translation added to the node's pivot. */
  position: Vec3;
  /** Euler rotation (radians) about the pivot. */
  rotation: Vec3;
  /** Per-axis scale about the pivot. */
  scale: Vec3;
}

const ZERO: Vec3 = [0, 0, 0];
const ONE: Vec3 = [1, 1, 1];

/** Wrap (loop) or clamp (one-shot) a time into a cycle of length `duration`. */
function cycleTime(t: number, duration: number, loop: boolean): number {
  if (duration <= 0) return 0;
  if (loop) return ((t % duration) + duration) % duration;
  return t < 0 ? 0 : t > duration ? duration : t;
}

/** Interpolate a scalar/tuple keyframe list at time `t`. */
function sampleChannel<T extends number | Vec3>(keys: Keyframe<T>[], t: number, fallback: T): T {
  if (keys.length === 0) return fallback;
  if (t <= keys[0]!.t) return keys[0]!.value;
  const last = keys[keys.length - 1]!;
  if (t >= last.t) return last.value;
  for (let i = 1; i < keys.length; i++) {
    const b = keys[i]!;
    if (t <= b.t) {
      const a = keys[i - 1]!;
      const span = b.t - a.t || 1;
      const x = ease(b.ease as Easing | undefined, (t - a.t) / span);
      return lerpValue(a.value, b.value, x) as T;
    }
  }
  return last.value;
}

function lerpValue(a: number | Vec3, b: number | Vec3, x: number): number | Vec3 {
  if (typeof a === "number" && typeof b === "number") return a + (b - a) * x;
  const av = toVec3(a), bv = toVec3(b);
  return [av[0] + (bv[0] - av[0]) * x, av[1] + (bv[1] - av[1]) * x, av[2] + (bv[2] - av[2]) * x];
}

function toVec3(v: number | Vec3): Vec3 {
  return typeof v === "number" ? [v, v, v] : v;
}

/** Sample a single track's node transform at the (already-cycled) time `t`. */
export function sampleTrack(track: Track, t: number): NodeTransform {
  return {
    position: sampleChannel(track.position ?? [], t, ZERO) as Vec3,
    rotation: sampleChannel(track.rotation ?? [], t, ZERO) as Vec3,
    scale: toVec3(sampleChannel(track.scale ?? [], t, ONE)),
  };
}

/**
 * Sample a whole animation at wall-clock time `t` (seconds since it started),
 * returning the node transform for every targeted part name. Loops or clamps
 * per {@link Animation.loop}. The returned map's keys are {@link Part.name}s.
 */
export function sampleAnimation(anim: Animation, t: number): Map<string, NodeTransform> {
  const ct = cycleTime(t, anim.duration, anim.loop ?? true);
  const out = new Map<string, NodeTransform>();
  for (const track of anim.tracks) out.set(track.target, sampleTrack(track, ct));
  return out;
}

/** The set of part names any of a model's animations can drive. */
export function animatedTargets(anims: Animation[]): Set<string> {
  const s = new Set<string>();
  for (const a of anims) for (const tr of a.tracks) s.add(tr.target);
  return s;
}

/** An interaction sampled at a moment: the model's own parts and the actor's
 *  rig parts (keyed by name; the actor map may carry the special `"root"`). */
export interface InteractionSample {
  /** Transforms for the model's own animated parts, keyed by {@link Part.name}. */
  self: Map<string, NodeTransform>;
  /** Transforms for the actor's rig parts, keyed by rig name (incl. `"root"`). */
  actor: Map<string, NodeTransform>;
}

/**
 * Sample an interaction at wall-clock time `t` (seconds), splitting its tracks
 * by {@link Track.subject} into the model's own transforms and the actor's. Pure
 * and environment-agnostic, exactly like {@link sampleAnimation}; loops or
 * clamps per {@link Interaction.loop}.
 */
export function sampleInteraction(it: Interaction, t: number): InteractionSample {
  const ct = cycleTime(t, it.duration, it.loop ?? true);
  const self = new Map<string, NodeTransform>();
  const actor = new Map<string, NodeTransform>();
  for (const tr of it.tracks) {
    const into = (tr.subject ?? "self") === "actor" ? actor : self;
    into.set(tr.target, sampleTrack(tr, ct));
  }
  return { self, actor };
}

/** The model's own part names any of its interactions' self tracks drive. */
export function selfInteractionTargets(interactions: Interaction[]): Set<string> {
  const s = new Set<string>();
  for (const it of interactions) {
    for (const tr of it.tracks) if ((tr.subject ?? "self") === "self") s.add(tr.target);
  }
  return s;
}

/** The pivot a track rotates/scales about (default the model origin). */
export function trackPivot(track: Track): Vec3 {
  return track.pivot ?? ZERO;
}
